/**
 * Google tag (gtag.js) loader for GA4 / Google Ads — ported from SkinPlug's
 * utils/gtag.ts (Janeks, 2026-10-20).
 *
 * Owns: loading the tag with the per-deploy IDs from GET /api/analytics/config
 * (backend env GOOGLE_TAG_IDS), SPA page views from main.ts's router, and
 * keeping secrets out of what Google sees.
 * Must not: read IDs from the bundle (the frontend image is built once in CI,
 * IDs differ per deploy) or track the admin panel.
 *
 * There is no cookie-consent UI on this site yet, so the tag loads as soon as
 * the IDs arrive — same as the plain gtag.js snippet would (TD-02).
 */
import { api } from '../../core/network/api';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

// Admin screens are internal tooling — keep them out of analytics.
const UNTRACKED_PATH = /^\/admin(-panel)?(\/|$)/;

// The only query params Google may see: campaign attribution. Everything else
// is dropped — an allowlist, because query strings here carry secrets
// (`/reset-password?token=`, `/verify-email?token=`, payment return refs).
const ATTRIBUTION_PARAM = /^(utm_[a-z]+|gclid|gbraid|wbraid|dclid)$/;

let loaded = false;
let currentPath: string | null = null;
let lastSentUrl: string | null = null;

/**
 * Make a URL safe to hand to Google. gtag.js would otherwise read
 * `document.location` / `document.referrer` raw, tokens included. Same-origin
 * URLs keep origin + path + attribution params; cross-origin referrers are
 * reduced to their origin; unparsable input becomes ''.
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return u.origin;
    const kept = new URLSearchParams();
    u.searchParams.forEach((value, name) => {
      if (ATTRIBUTION_PARAM.test(name)) kept.append(name, value);
    });
    const query = kept.toString();
    return u.origin + u.pathname + (query ? `?${query}` : '');
  } catch {
    return '';
  }
}

function gtag(...args: unknown[]) {
  window.gtag?.(...args);
}

/**
 * Override gtag.js's own reads of `document.location` / `document.referrer`
 * for every hit it sends, including automatic ones (engagement, scroll).
 * The first referrer is the external one; SPA navigations refer to the
 * previous in-app page.
 */
function setSafeLocation(fullPath: string) {
  gtag('set', {
    page_location: sanitizeUrl(window.location.origin + fullPath),
    page_referrer: lastSentUrl ?? sanitizeUrl(document.referrer),
  });
}

function sendPageView(fullPath: string) {
  const location = sanitizeUrl(window.location.origin + fullPath);
  const path = location.slice(window.location.origin.length);
  // Consecutive identical URLs are one view — e.g. a screen replacing its
  // token-bearing URL with the bare path.
  if (UNTRACKED_PATH.test(path) || location === lastSentUrl) return;
  setSafeLocation(fullPath);
  gtag('event', 'page_view', {
    page_path: path,
    page_location: location,
    page_title: document.title,
  });
  lastSentUrl = location;
}

function load(tagIds: string[]) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the `arguments` object itself, not a copied array.
    // eslint-disable-next-line prefer-rest-params -- gtag.js contract, see above
    window.dataLayer!.push(arguments);
  };
  gtag('js', new Date());
  // Before any config: gtag.js must never see the raw URL (it may hold a token).
  setSafeLocation(currentPath ?? window.location.pathname);
  // Page views are sent by trackPageView so SPA navigations are counted once.
  for (const id of tagIds) gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagIds[0])}`;
  document.head.appendChild(script);
  loaded = true;

  if (currentPath) sendPageView(currentPath);
}

/** Load the tag with the IDs the backend serves. Call once at boot; a failure only means no analytics. */
export async function initGoogleTags(): Promise<void> {
  if (loaded) return;
  try {
    const { googleTagIds } = await api.get<{ googleTagIds: string[] }>('/analytics/config');
    if (googleTagIds?.length && !loaded) load(googleTagIds);
  } catch {
    // Analytics is optional; the game must never wait on or break over it.
  }
}

/** Record the screen the router just rendered. Safe to call before the tag loads. */
export function trackPageView(fullPath: string) {
  // Raw path is kept locally only; sanitizeUrl runs before anything is sent.
  currentPath = fullPath;
  if (loaded) sendPageView(fullPath);
}
